import { useCallback, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { IUseAreasReorderArgs } from "./interfaces";

export const useAreasReorder = ({ areas, setAreas, refetch, setAreasOrder }: IUseAreasReorderArgs) => {
    const [isDraggingAreas, setIsDraggingAreas] = useState(false);

    const onDragMove = useCallback(() => {
        setIsDraggingAreas(true);
    }, []);

    const onDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            setIsDraggingAreas(false);

            if (active.id !== over?.id && areas && setAreas) {
                // Areas are already sorted by order from the GraphQL query
                const sortedAreas = areas;

                // Find indices in the sorted array
                const oldIndex = sortedAreas.findIndex((area) => area.area_id.toString() === active.id);
                const newIndex = sortedAreas.findIndex((area) => area.area_id.toString() === over?.id);

                const newAreas = arrayMove(sortedAreas, oldIndex, newIndex);

                // Update local state immediately for visual feedback
                setAreas(newAreas);

                try {
                    // Update with null order to avoid conflict on same order by machine center constraint
                    await setAreasOrder({
                        variables: {
                            areas: newAreas.map((area) => {
                                return {
                                    area_id: area.area_id,
                                    order: null,
                                    machine_center_id: area.machine_center_id,
                                    name_translation_id: area.translation.id,
                                };
                            }),
                        },
                    });
                    // Update with new ordering
                    await setAreasOrder({
                        variables: {
                            areas: newAreas.map((area, index) => {
                                const orderNumber = index + 1;
                                return {
                                    area_id: area.area_id,
                                    order: orderNumber,
                                    machine_center_id: area.machine_center_id,
                                    name_translation_id: area.translation.id,
                                };
                            }),
                        },
                    });
                    await refetch();
                } catch (error) {
                    console.error("Failed to update areas order", error);
                    // Revert local state on error
                    setAreas(areas);
                }
            }
        },
        [areas, refetch, setAreasOrder, setAreas],
    );

    return { isDraggingAreas, onDragMove, onDragEnd };
};
